import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, LogOut } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminChat from "@/pages/admin/AdminChat";
import AdminSync from "@/pages/admin/AdminSync";
import AdminStats from "@/pages/admin/AdminStats";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminVideos from "@/pages/admin/AdminVideos";
import PushAlertsCard from "@/pages/admin/PushAlertsCard";

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [auth, setAuth] = useState(null);
  const [newInquiries, setNewInquiries] = useState(0);

  useEffect(() => {
    if (!auth || auth === false) return undefined;
    const poll = () =>
      api.get("/inquiries", { params: { status: "new" } }).then((r) => setNewInquiries((r.data || []).length)).catch(() => {});
    poll();
    const iv = setInterval(poll, 60000);
    const onChanged = () => poll();
    window.addEventListener("psm-inquiries-changed", onChanged);
    return () => {
      clearInterval(iv);
      window.removeEventListener("psm-inquiries-changed", onChanged);
    };
  }, [auth]);

  const location = useLocation();
  const oauthProcessed = useRef(false);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const hasSessionId = location.hash?.includes("session_id=");

  useEffect(() => {
    const onUnauthorized = () => setAuth(false);
    window.addEventListener("psm-unauthorized", onUnauthorized);
    if (hasSessionId) {
      if (!oauthProcessed.current) {
        oauthProcessed.current = true;
        const sessionId = (location.hash.match(/session_id=([^&]+)/) || [])[1];
        window.history.replaceState(null, "", window.location.pathname);
        api.post("/auth/google/session", { session_id: sessionId })
          .then((r) => {
            localStorage.setItem("psm_admin_token", r.data.token);
            setAuth(r.data.user);
          })
          .catch((err) => {
            toast.error(err?.response?.data?.detail || "Google sign-in failed");
            setAuth(false);
          });
      }
      return () => window.removeEventListener("psm-unauthorized", onUnauthorized);
    }
    const token = localStorage.getItem("psm_admin_token");
    if (!token) {
      setAuth(false);
    } else {
      api.get("/auth/me").then((r) => setAuth(r.data)).catch(() => setAuth(false));
    }
    return () => window.removeEventListener("psm-unauthorized", onUnauthorized);
    // Intentionally mount-only: OAuth hash processing + session restore must run exactly once (oauthProcessed guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("psm_admin_token");
    setAuth(false);
  };

  if (auth === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (auth === false) {
    return <AdminLogin onLogin={(user) => setAuth(user)} />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="admin-page">
      <header className="border-b border-soft bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand">
            <img src={LOGO_URL} alt="Pit Stop Motors" className="h-12 w-auto rounded-sm" />
            <div className="leading-none">
              <p className="overline text-[10px] !text-accent-bright">Admin Dashboard</p>
              <p className="font-serif text-xl mt-1">Idaho · DLR # 0448</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-primary-foreground/70 hover:text-accent-bright inline-flex items-center gap-1" data-testid="admin-view-site">
              <HomeIcon className="h-3.5 w-3.5" /> View site
            </Link>
            <button onClick={logout} className="text-sm text-primary-foreground/70 hover:text-accent-bright inline-flex items-center gap-1" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 md:px-10 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-secondary rounded-sm h-auto p-1 flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="dashboard" className="rounded-sm" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="inventory" className="rounded-sm" data-testid="tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="inquiries" className="rounded-sm relative" data-testid="tab-inquiries">
              Inquiries
              {newInquiries > 0 && (
                <span
                  className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-bold text-accent-foreground"
                  data-testid="inquiries-badge"
                >
                  {newInquiries}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-sm" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="videos" className="rounded-sm" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="chat" className="rounded-sm" data-testid="tab-chat">Live Chat</TabsTrigger>
            <TabsTrigger value="sync" className="rounded-sm" data-testid="tab-sync">Sync / Import</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-8">
            <PushAlertsCard />
            <div className="mt-8"><AdminStats /></div>
          </TabsContent>
          <TabsContent value="inventory" className="mt-8"><AdminInventory /></TabsContent>
          <TabsContent value="inquiries" className="mt-8"><AdminInquiries /></TabsContent>
          <TabsContent value="reviews" className="mt-8"><AdminReviews /></TabsContent>
          <TabsContent value="videos" className="mt-8"><AdminVideos /></TabsContent>
          <TabsContent value="chat" className="mt-8"><AdminChat /></TabsContent>
          <TabsContent value="sync" className="mt-8"><AdminSync /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
